import { useEffect, useState } from "react";
import { Copy, Check, Trash2, Mail, Send, RefreshCw, Search, ExternalLink, Share2, Eye } from "lucide-react";
import { toast } from "sonner";
import { adminListSharedCarts, adminDeleteSharedCart, adminEmailSharedCart } from "../../lib/api";

/**
 * Admin "Shared Carts" tab.
 *
 * Lists every shared-cart link the storefront has ever minted, newest
 * first, with status (Active / Used / Expired), customer info, cart
 * total, and quick actions: copy URL, email URL (mailto:), delete.
 *
 * Heavy `items` payloads are excluded from the list endpoint to keep
 * the response small even with hundreds of saved carts.
 */
export default function SharedCartsTab({ pw }) {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const reload = () => {
    setLoading(true);
    adminListSharedCarts(pw)
      .then((r) => setCarts(r.shared_carts || []))
      .catch(() => toast.error("Failed to load shared carts"))
      .finally(() => setLoading(false));
  };

  useEffect(reload, [pw]);  // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = carts.filter((c) => {
    if (!q.trim()) return true;
    const hay = `${c.customer_name || ""} ${c.customer_email || ""} ${c.notes || ""} ${c.token || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const copyLink = async (token, id) => {
    const url = `${window.location.origin}/shared-cart/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — link is " + url);
    }
  };

  const remove = async (cart) => {
    if (!window.confirm(`Delete shared cart for ${cart.customer_email || cart.customer_name || "unknown"}? This cannot be undone.`)) return;
    try {
      await adminDeleteSharedCart(cart.id, pw);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  /**
   * Server-side email send. Fires the link from torontocustomt@gmail.com
   * with the branded template + inline mockup preview + open-tracking pixel.
   * Falls back to a mailto: handoff if the recipient email isn't on the
   * record (admin can re-open the modal to fix that).
   */
  const sendEmail = async (cart) => {
    if (!cart.customer_email) {
      const entered = window.prompt(`No email is saved on this cart. Enter the customer's email to send the link to:`);
      if (!entered || !entered.includes("@")) return;
      cart = { ...cart, _override_email: entered };
    }
    if (cart.status !== "active") {
      toast.error("Can only email Active links");
      return;
    }
    if (!window.confirm(`Email the cart link to ${cart._override_email || cart.customer_email}?`)) return;
    setSendingId(cart.id);
    try {
      const res = await adminEmailSharedCart(
        cart.id,
        cart._override_email ? { to_email: cart._override_email } : null,
        pw,
      );
      if (res.sent) toast.success(`Sent to ${res.to}`);
      else toast.warning("Email backend not configured — link would have gone to " + res.to);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="shared-carts-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Share2 size={20} className="text-[var(--tc-red)]" />
          <h2 className="font-display text-3xl uppercase">Shared Carts</h2>
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">· {carts.length} TOTAL</span>
        </div>
        <button
          onClick={reload}
          data-testid="shared-carts-refresh"
          className="p-2 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
        <input
          data-testid="shared-carts-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search customer name, email, or notes…"
          className="w-full bg-[#0d0d0d] border border-[#222] pl-9 pr-3 py-2 font-mono text-xs outline-none focus:border-[var(--tc-red)]"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center text-zinc-500 font-mono text-xs tracking-widest">LOADING…</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 border border-[#222] text-center" data-testid="shared-carts-empty">
          <Share2 size={32} className="mx-auto text-zinc-700 mb-3" />
          <div className="font-ui uppercase tracking-wider text-sm">No shared carts yet</div>
          <p className="text-zinc-500 text-xs mt-2">
            Use the &ldquo;Share Cart&rdquo; button on the cart page to mint a private link for a customer.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#222]">
          <table className="w-full text-sm font-body">
            <thead>
              <tr className="bg-[#0d0d0d] text-left font-mono text-[10px] tracking-widest text-zinc-500 uppercase">
                <th className="p-3">Customer</th>
                <th className="p-3 hidden md:table-cell">Created</th>
                <th className="p-3 hidden lg:table-cell">Expires</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const url = `${window.location.origin}/shared-cart/${c.token}`;
                const used = c.status === "used";
                const expired = c.status === "expired";
                const active = !used && !expired;
                return (
                  <tr key={c.id} data-testid={`shared-cart-row-${c.id}`} className="border-t border-[#222] hover:bg-[#0d0d0d]">
                    <td className="p-3 min-w-[180px]">
                      <div className="font-bold tracking-wide text-zinc-200">{c.customer_name || <span className="text-zinc-500">—</span>}</div>
                      {c.customer_email && <div className="font-mono text-[10px] text-zinc-500">{c.customer_email}</div>}
                      <div className="font-mono text-[9px] text-zinc-600 mt-1">
                        {c.items_count} item{c.items_count === 1 ? "" : "s"}
                        {c.created_by_admin && <span className="ml-2 text-[var(--tc-red)]">· ADMIN</span>}
                      </div>
                    </td>
                    <td className="p-3 font-mono text-[10px] text-zinc-500 hidden md:table-cell whitespace-nowrap">
                      {fmtDate(c.created_at)}
                    </td>
                    <td className="p-3 font-mono text-[10px] text-zinc-500 hidden lg:table-cell whitespace-nowrap">
                      {fmtDate(c.expires_at)}
                    </td>
                    <td className="p-3 text-right font-mono">${(c.cart_total || 0).toFixed(2)}</td>
                    <td className="p-3 text-center">
                      <span
                        data-testid={`shared-cart-status-${c.id}`}
                        className={`inline-flex px-2 py-0.5 border text-[9px] font-mono tracking-widest uppercase ${
                          used ? "border-[var(--tc-blue)] text-[var(--tc-blue)]" :
                          expired ? "border-zinc-700 text-zinc-500" :
                          "border-[var(--tc-red)] text-[var(--tc-red)]"
                        }`}
                      >
                        {used ? `Used · ${c.used_order_number || "✓"}` : expired ? "Expired" : "Active"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => copyLink(c.token, c.id)}
                          data-testid={`shared-cart-copy-${c.id}`}
                          className="p-1.5 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] transition-colors"
                          aria-label="Copy link"
                          title="Copy link"
                          disabled={!active}
                        >
                          {copiedId === c.id ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                        {c.customer_email && active && (
                          <a
                            href={`mailto:${c.customer_email}?subject=${encodeURIComponent("Your custom apparel cart is ready")}&body=${encodeURIComponent(`Hi${c.customer_name ? " " + c.customer_name : ""},\n\nHere's the cart we set up for you — review and pay when ready:\n\n${url}\n\n— Toronto Custom T-Shirts`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`shared-cart-email-${c.id}`}
                            className="p-1.5 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] transition-colors"
                            aria-label="Email link"
                            title="Email link"
                          >
                            <Mail size={12} />
                          </a>
                        )}
                        {active && (
                          <button
                            onClick={() => sendEmail(c)}
                            data-testid={`shared-cart-send-${c.id}`}
                            disabled={sendingId === c.id}
                            className="p-1.5 border border-[var(--tc-red)] text-[var(--tc-red)] hover:bg-[var(--tc-red)] hover:text-white transition-colors disabled:opacity-50"
                            aria-label="Send link via email"
                            title="Send link from torontocustomt@gmail.com"
                          >
                            {sendingId === c.id ? <RefreshCw size={12} className="animate-spin" /> : <Send size={12} />}
                          </button>
                        )}
                        {(c.email_send_count > 0 || c.open_count > 0) && (
                          <span
                            className="hidden md:inline-flex items-center gap-1 px-1.5 font-mono text-[9px] tracking-widest text-zinc-500"
                            title={`Sent ${c.email_send_count || 0} time(s) · Opened ${c.open_count || 0} time(s)`}
                            data-testid={`shared-cart-tracking-${c.id}`}
                          >
                            <Eye size={10} /> {c.open_count || 0}/{c.email_send_count || 0}
                          </span>
                        )}
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`shared-cart-open-${c.id}`}
                          className="p-1.5 border border-[#222] hover:border-white hover:text-white transition-colors"
                          aria-label="Open link"
                          title="Open link"
                        >
                          <ExternalLink size={12} />
                        </a>
                        <button
                          onClick={() => remove(c)}
                          data-testid={`shared-cart-delete-${c.id}`}
                          className="p-1.5 border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] transition-colors"
                          aria-label="Delete"
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}
